import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import type { MessagePart } from './Message'
import { isHoverPreviewOfferType, type ChatLinkHoverTarget } from './ChatLinkHoverPreview'
import { HOVER_CLOSE_DELAY_MS } from './PlaceHoverPreview/constants'
import type { LinkRect } from './PlaceHoverPreview/types'

interface UseChatLinkInteractionsOptions {
  markdownContainerRef: RefObject<HTMLDivElement | null>
  canShowHover: boolean
  contentKey: string
  failedPlaceQueries: ReadonlySet<string>
  messageId?: string
  onOfferClick: (data: { offerId: string; offerType?: string }) => void
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  onPlaceSearchClick: (data: { searchQuery: string; placeName: string }) => void
}

interface OfferClickData {
  offerId: string
  offerType?: string
}

function getLinkRect(element: Element): LinkRect {
  const rect = element.getBoundingClientRect()
  return {
    top: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height,
  }
}

function getOfferClickData(target: HTMLElement): OfferClickData | undefined {
  const offerId = target.dataset.offerId
  const offerType = target.dataset.offerType
  if (!offerId) {
    return undefined
  }

  return {
    offerId,
    ...(offerType ? { offerType } : {}),
  }
}

function getChatLinkTarget(event: Event): HTMLElement | undefined {
  const target = event.target
  if (!(target instanceof Element)) {
    return undefined
  }

  const placeTag = target.closest('.place-tag')
  if (placeTag instanceof HTMLElement) {
    return placeTag
  }

  const offerTag = target.closest('.offer-tag')
  if (offerTag instanceof HTMLElement) {
    return offerTag
  }

  return undefined
}

/** Stable key for markdown link wiring — tracks rendered text, not just parts array identity. */
export function getChatLinkContentKey(parts: ReadonlyArray<MessagePart>): string {
  return parts.map(part => {
    if (part.type === 'text') {
      return part.content
    }
    return JSON.stringify(part)
  }).join('\x1e')
}

export function useChatLinkInteractions({
  markdownContainerRef,
  canShowHover,
  contentKey,
  failedPlaceQueries,
  messageId,
  onOfferClick,
  onPlaceClick,
  onPlaceSearchClick,
}: UseChatLinkInteractionsOptions) {
  const [hoverTarget, setHoverTarget] = useState<ChatLinkHoverTarget | undefined>()
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const onOfferClickRef = useRef(onOfferClick)
  const onPlaceClickRef = useRef(onPlaceClick)
  const onPlaceSearchClickRef = useRef(onPlaceSearchClick)
  const openHoverTargetRef = useRef<(target: ChatLinkHoverTarget) => void>(() => {})

  onOfferClickRef.current = onOfferClick
  onPlaceClickRef.current = onPlaceClick
  onPlaceSearchClickRef.current = onPlaceSearchClick

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = undefined
    }
  }, [])

  const openHoverTarget = useCallback((target: ChatLinkHoverTarget) => {
    clearHoverCloseTimeout()
    setHoverTarget(target)
  }, [clearHoverCloseTimeout])

  openHoverTargetRef.current = openHoverTarget

  const scheduleHoverClose = useCallback(() => {
    clearHoverCloseTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverTarget(undefined)
      hoverCloseTimeoutRef.current = undefined
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearHoverCloseTimeout])

  const closeHoverTarget = useCallback(() => {
    clearHoverCloseTimeout()
    setHoverTarget(undefined)
  }, [clearHoverCloseTimeout])

  useEffect(() => {
    return () => clearHoverCloseTimeout()
  }, [clearHoverCloseTimeout])

  useEffect(() => {
    clearHoverCloseTimeout()
    setHoverTarget(undefined)
  }, [
    canShowHover,
    clearHoverCloseTimeout,
    contentKey,
    failedPlaceQueries,
    messageId,
  ])

  useEffect(() => {
    const container = markdownContainerRef.current
    if (!container) return

    const activatePlaceTag = (target: HTMLElement) => {
      if (target.classList.contains('place-tag--failed')) {
        return
      }

      const placeId = target.dataset.placeId
      const placeSearchQuery = target.dataset.placeSearch
      const placeName = target.textContent || target.dataset.placeName || ''
      if (placeId) {
        onPlaceClickRef.current({ googlePlaceId: placeId, placeName })
      } else if (placeSearchQuery) {
        onPlaceSearchClickRef.current({ searchQuery: placeSearchQuery, placeName })
      }
    }

    const activateOfferTag = (target: HTMLElement) => {
      const offerClickData = getOfferClickData(target)
      if (offerClickData) {
        onOfferClickRef.current(offerClickData)
      }
    }

    const handleClick = (event: Event) => {
      const target = getChatLinkTarget(event)
      if (!target) return

      if (target.classList.contains('place-tag')) {
        activatePlaceTag(target)
        return
      }

      activateOfferTag(target)
    }

    const handleKeyDown = (event: Event) => {
      if (!(event instanceof KeyboardEvent)) {
        return
      }
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }

      const target = getChatLinkTarget(event)
      if (!target?.classList.contains('offer-tag')) {
        return
      }

      event.preventDefault()
      activateOfferTag(target)
    }

    container.addEventListener('click', handleClick)
    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('click', handleClick)
      container.removeEventListener('keydown', handleKeyDown)
    }
  }, [markdownContainerRef])

  useEffect(() => {
    const container = markdownContainerRef.current
    if (!container) return
    const cleanups: Array<() => void> = []

    container.querySelectorAll('.place-tag').forEach(tag => {
      if (!(tag instanceof HTMLElement)) {
        return
      }

      const placeSearch = tag.dataset.placeSearch

      if (placeSearch) {
        if (failedPlaceQueries.has(placeSearch)) {
          tag.classList.add('place-tag--failed')
        } else {
          tag.classList.remove('place-tag--failed')
        }
      }

      if (!canShowHover || tag.classList.contains('place-tag--failed')) {
        return
      }

      const hoverEnterHandler = (event: Event) => {
        const target = event.currentTarget
        if (!(target instanceof HTMLElement)) return
        const placeId = target.dataset.placeId
        const placeSearchQuery = target.dataset.placeSearch
        const placeName = target.textContent || target.dataset.placeName || ''

        if (placeId) {
          openHoverTargetRef.current({
            type: 'place',
            placeId,
            placeName,
            rect: getLinkRect(target),
          })
          return
        }

        if (placeSearchQuery) {
          openHoverTargetRef.current({
            type: 'placeSearch',
            searchQuery: placeSearchQuery,
            placeName,
            rect: getLinkRect(target),
          })
        }
      }

      tag.addEventListener('mouseenter', hoverEnterHandler)
      tag.addEventListener('mouseleave', scheduleHoverClose)
      cleanups.push(() => tag.removeEventListener('mouseenter', hoverEnterHandler))
      cleanups.push(() => tag.removeEventListener('mouseleave', scheduleHoverClose))
    })

    container.querySelectorAll('.offer-tag').forEach(tag => {
      if (!(tag instanceof HTMLElement)) {
        return
      }

      if (!canShowHover) {
        return
      }

      const hoverEnterHandler = (event: Event) => {
        const target = event.currentTarget
        if (!(target instanceof HTMLElement)) return
        const offerId = target.dataset.offerId
        const offerType = target.dataset.offerType
        const offerName = target.textContent || target.dataset.offerName || ''
        if (!offerId || !isHoverPreviewOfferType(offerType)) return

        openHoverTargetRef.current({
          type: 'offer',
          offerId,
          offerType,
          offerName,
          rect: getLinkRect(target),
        })
      }

      tag.addEventListener('mouseenter', hoverEnterHandler)
      tag.addEventListener('mouseleave', scheduleHoverClose)
      cleanups.push(() => tag.removeEventListener('mouseenter', hoverEnterHandler))
      cleanups.push(() => tag.removeEventListener('mouseleave', scheduleHoverClose))
    })

    return () => cleanups.forEach(fn => fn())
  }, [
    canShowHover,
    contentKey,
    failedPlaceQueries,
    markdownContainerRef,
    messageId,
    scheduleHoverClose,
  ])

  return {
    hoverTarget,
    closeHoverTarget,
    keepHoverTargetOpen: clearHoverCloseTimeout,
  } as const
}
